import { getRealtimeOutboxDiagnostics } from "./realtime/realtime-outbox.js";
import {
  createDisabledWakeSignalDiagnostics,
  type WakeSignalDiagnosticsSnapshot,
} from "./wake-signal/wake-signal-diagnostics.js";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "error">;

type RouteContext = {
  req: {
    header(name: string): string | undefined;
  };
  json(body: unknown, status?: number): unknown;
};

type RouteRegistrar = {
  get(path: string, handler: (context: RouteContext) => Promise<unknown> | unknown): void;
};

const REALTIME_OUTBOX_DIAGNOSTICS_INTERNAL_ERROR =
  "Internal realtime outbox diagnostics failed";

function isAuthorizedInternalToken(token: string | undefined, internalToken: string | undefined): boolean {
  return Boolean(internalToken && token === internalToken);
}

export function createRealtimeOutboxDiagnosticsRouteHandler(params: {
  rawSql: RawSql;
  internalToken?: string;
  now?: () => Date;
  getWakeSignalDiagnostics?: () => Promise<WakeSignalDiagnosticsSnapshot> | WakeSignalDiagnosticsSnapshot;
  logger?: LoggerLike;
}): (context: RouteContext) => Promise<unknown> {
  const logger = params.logger ?? console;
  return async (c) => {
    if (!isAuthorizedInternalToken(c.req.header("X-Internal-Token"), params.internalToken)) {
      return c.json({ error: "Forbidden" }, 403);
    }

    try {
      const diagnostics = await getRealtimeOutboxDiagnostics({
        rawSql: params.rawSql,
        now: params.now?.(),
      });
      const wakeSignal =
        (await params.getWakeSignalDiagnostics?.()) ?? createDisabledWakeSignalDiagnostics("disabled");
      return c.json({
        ok: true,
        realtimeOutbox: diagnostics,
        wakeSignal,
      });
    } catch (error) {
      logger.error("[realtime-outbox] diagnostics failed");
      return c.json({ error: REALTIME_OUTBOX_DIAGNOSTICS_INTERNAL_ERROR }, 500);
    }
  };
}

export function registerRealtimeOutboxDiagnosticsRoute(params: {
  app: RouteRegistrar;
  rawSql: RawSql;
  internalToken?: string;
  getWakeSignalDiagnostics?: () => Promise<WakeSignalDiagnosticsSnapshot> | WakeSignalDiagnosticsSnapshot;
  logger?: LoggerLike;
}): void {
  params.app.get(
    "/internal/realtime-outbox/diagnostics",
    createRealtimeOutboxDiagnosticsRouteHandler({
      rawSql: params.rawSql,
      internalToken: params.internalToken,
      getWakeSignalDiagnostics: params.getWakeSignalDiagnostics,
      logger: params.logger,
    }),
  );
}
